import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  TextField,
  Grid,
  Divider,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { hybridWhatsAppService, WhatsAppServiceStatus } from '../../services/hybridWhatsAppService';

const HybridWhatsAppManager: React.FC = () => {
  const [status, setStatus] = useState<WhatsAppServiceStatus>({ available: false, method: 'none' });
  const [testMessage, setTestMessage] = useState('مرحباً! هذه رسالة تجريبية من نظام الصيانة.');
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // الحصول على حالة الخدمة
    const serviceStatus = hybridWhatsAppService.getStatus();
    setStatus(serviceStatus);
  }, []);

  const handleSendTestMessage = async () => {
    if (!testPhone || !testMessage) {
      alert('يرجى إدخال رقم الهاتف والرسالة');
      return;
    }

    setSending(true);
    try {
      const success = await hybridWhatsAppService.sendMessage('deviceReceived', {
        customerName: 'عميل تجريبي',
        customerPhone: testPhone,
        deviceType: 'هاتف ذكي',
        deviceSize: 'كبير',
        cost: '100 ريال',
        orderNumber: 12345,
        estimatedDate: new Date().toLocaleDateString('ar-SA')
      });

      if (success) {
        alert('تم فتح WhatsApp بنجاح! تحقق من التطبيق لإرسال الرسالة.');
      } else {
        alert('فشل في إرسال الرسالة');
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      alert('حدث خطأ أثناء إرسال الرسالة');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = () => {
    if (status.available && status.method === 'wa.me') return 'success';
    if (status.available && status.method === 'baileys') return 'info';
    return 'warning';
  };

  const getStatusText = () => {
    if (status.method === 'wa.me') return 'متاح - wa.me links';
    if (status.method === 'baileys') return 'متاح - Baileys';
    return 'غير متاح';
  };

  const features = [
    {
      icon: <SpeedIcon color="primary" />,
      title: 'سرعة الإرسال',
      description: 'فتح فوري لتطبيق WhatsApp مع الرسالة جاهزة'
    },
    {
      icon: <SecurityIcon color="success" />,
      title: 'أمان عالي',
      description: 'لا توجد خوادم وسطية - اتصال مباشر مع WhatsApp'
    },
    {
      icon: <LanguageIcon color="info" />,
      title: 'دعم العربية',
      description: 'قوالب رسائل احترافية باللغة العربية'
    },
    {
      icon: <MessageIcon color="warning" />,
      title: 'رسائل ذكية',
      description: 'قوالب متخصصة لكل مرحلة من مراحل الصيانة'
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <WhatsAppIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h5" fontWeight="bold">
                خدمة WhatsApp الذكية
              </Typography>
            </Box>
            <Chip
              icon={<CheckIcon />}
              label={getStatusText()}
              color={getStatusColor()}
              variant="outlined"
            />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              هذه الخدمة تستخدم روابط wa.me لفتح WhatsApp مباشرة مع الرسالة جاهزة للإرسال.
              لا حاجة لتثبيت مكتبات إضافية أو إعدادات معقدة.
            </Typography>
          </Alert>

          {/* Features Grid */}
          <Grid container spacing={2}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    {feature.icon}
                    <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Test Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🧪 اختبار إرسال رسالة
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="رقم الهاتف"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="966501234567"
                helperText="أدخل رقم الهاتف مع رمز الدولة"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="الرسالة"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="رسالة تجريبية"
                helperText="سيتم استخدام قالب رسالة الصيانة"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendTestMessage}
                disabled={sending || !testPhone}
                fullWidth
                size="large"
              >
                {sending ? 'جاري الإعداد...' : 'إرسال رسالة تجريبية'}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* How it works */}
          <Typography variant="h6" gutterBottom>
            📋 كيف تعمل الخدمة
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <Typography variant="body2" sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 24, 
                  height: 24, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.75rem'
                }}>
                  1
                </Typography>
              </ListItemIcon>
              <ListItemText 
                primary="إعداد الرسالة"
                secondary="يتم إعداد الرسالة باستخدام قالب احترافي مع بيانات العميل والجهاز"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Typography variant="body2" sx={{ 
                  bgcolor: 'success.main', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 24, 
                  height: 24, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.75rem'
                }}>
                  2
                </Typography>
              </ListItemIcon>
              <ListItemText 
                primary="فتح WhatsApp"
                secondary="يتم فتح تطبيق WhatsApp تلقائياً مع الرسالة جاهزة للإرسال"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Typography variant="body2" sx={{ 
                  bgcolor: 'info.main', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 24, 
                  height: 24, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.75rem'
                }}>
                  3
                </Typography>
              </ListItemIcon>
              <ListItemText 
                primary="إرسال الرسالة"
                secondary="تحقق من الرسالة وأرسلها بنقرة واحدة من تطبيق WhatsApp"
              />
            </ListItem>
          </List>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              💡 <strong>نصيحة:</strong> تأكد من أن تطبيق WhatsApp مثبت على جهازك للحصول على أفضل تجربة.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HybridWhatsAppManager;
